// pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { User, Briefcase, FileText, Upload, Settings as SettingsIcon, Shield, ChevronRight, Home, Menu, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import PersonalInfo from './components/PersonalInfo';
import ProfessionalInfo from './components/ProfessionalInfo';
import ResumeManagement from './components/ResumeManagement';
import Documents from './components/Documents';
import Preferences from './components/Preferences';
import Security from './components/Security';
import { environment } from '@/environments/environment';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
 
const TABS = [
{ id: 'personal', label: 'Personal Info', icon: User, description: 'Manage your personal details', color: 'blue' },
{ id: 'professional', label: 'Professional', icon: Briefcase, description: 'Career preferences & goals', color: 'purple' },
{ id: 'resume', label: 'Resume', icon: FileText, description: 'Update your resume sections', color: 'green' },
{ id: 'documents', label: 'Documents', icon: Upload, description: 'Certificates & awards', color: 'amber' },
{ id: 'preferences', label: 'Preferences', icon: SettingsIcon, description: 'App settings & notifications', color: 'pink' },
{ id: 'security', label: 'Security', icon: Shield, description: 'Password & privacy', color: 'red' },
];

const Settings = () => {
const navigate = useNavigate();
const [activeTab, setActiveTab] = useState('personal');
const [isMobile, setIsMobile] = useState(false);
const [sidebarOpen, setSidebarOpen] = useState(false);
const [isTransitioning, setIsTransitioning] = useState(false);

useEffect(() => {
const checkMobile = () => {
const mobile = window.innerWidth < 768;
setIsMobile(mobile);
if (!mobile) setSidebarOpen(false);
};
checkMobile();
window.addEventListener('resize', checkMobile);
return () => window.removeEventListener('resize', checkMobile);
}, []);

// Smooth tab transitions
const handleTabChange = (tabId) => {
setIsTransitioning(true);
setTimeout(() => {
setActiveTab(tabId);
setIsTransitioning(false);
setSidebarOpen(false);
// Scroll to top smoothly
window.scrollTo({ top: 0, behavior: 'smooth' });
}, 150);
};

const renderTabContent = () => {
const content = (() => {
switch (activeTab) {
case 'personal':
return <PersonalInfo />;
case 'professional':
return <ProfessionalInfo />;
case 'resume':
return <ResumeManagement />;
case 'documents':
return <Documents />;
case 'preferences':
return <Preferences />;
case 'security':
return <Security />;
default:
return <PersonalInfo />;
}
})();

return (
<div 
className={`transition-all duration-150 ${
isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
}`}
>
{content}
</div>
);
};

const activeTabData = TABS.find(tab => tab.id === activeTab);

return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
<Header />

<main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
<div className="max-w-7xl mx-auto">
{/* Enhanced Header with Breadcrumb */}
<div className="mb-6">
{/* Breadcrumb */}


{/* Title and Description */}
<div className="flex items-center justify-between">
<div>
<h1 className="text-xl lg:text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
<span className="text-md lg:text-3xl">⚙️</span>
Settings
</h1>
<p className="text-text-secondary text-sm">Manage your account and preferences</p>
</div>

{/* Mobile Menu Button */}
{isMobile && (
<button
onClick={() => setSidebarOpen(!sidebarOpen)}
className="lg:hidden p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-indigo-300 transition-all shadow-sm"
>
{sidebarOpen ? <X size={24} /> : <Menu size={24} />}
</button>
)}
</div>
</div>

<div className="flex flex-col md:flex-row gap-6 relative">
{/* Mobile Overlay */}
{isMobile && sidebarOpen && (
<div 
className="fixed inset-0 bg-black/50 z-40 md:hidden"
onClick={() => setSidebarOpen(false)}
/>
)}

{/* Sidebar Navigation */}
<aside className={`
md:w-72 flex-shrink-0
${isMobile ? 'fixed inset-y-0 left-0 z-50 w-80 transform transition-transform duration-300' : ''}
${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
`}>
<nav className={`
bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden
${isMobile ? 'h-full mt-4 ml-4 mr-4' : ''}
`}>
{/* Sidebar Header (Mobile) */}
{isMobile && (
<div className="p-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
<div className="flex items-center justify-between">
<h2 className="font-bold text-slate-900">Navigation</h2>
<button
onClick={() => setSidebarOpen(false)}
className="p-1 hover:bg-white rounded-lg transition-all"
>
<X size={20} />
</button>
</div>
</div>
)}

{/* Tab Buttons */}
<div className={isMobile ? 'overflow-y-auto max-h-[calc(100vh-120px)] p-2' : ''}>
{TABS.map((tab, index) => {
const Icon = tab.icon;
const isActive = activeTab === tab.id;

return (
<button
key={tab.id}
onClick={() => handleTabChange(tab.id)}
className={`
w-full flex items-start gap-4 px-5 py-4 text-left transition-all duration-200 rounded-xl
${isMobile ? 'mb-2' : ''}
${isActive 
? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-600 shadow-sm' 
: 'hover:bg-slate-50 border-l-4 border-transparent hover:border-slate-200'
}
group
`}
style={{
animationDelay: `${index * 50}ms`
}}
>
<div className={`
p-2 rounded-lg transition-all
${isActive 
? 'bg-indigo-100 text-indigo-600' 
: 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
}
`}>
<Icon size={20} />
</div>

<div className="flex-1 min-w-0">
<p className={`font-semibold mb-0.5 ${
isActive ? 'text-indigo-900' : 'text-slate-700'
}`}>
{tab.label}
</p>
<p className={`text-xs ${
isActive ? 'text-indigo-600' : 'text-slate-500'
}`}>
{tab.description}
</p>
</div>

{isActive && (
<ChevronRight 
className="text-indigo-600 flex-shrink-0 mt-1" 
size={20} 
/>
)}
</button>
);
})}
</div>

{/* Help Section */}
<div className="p-4 border-t border-slate-200 bg-slate-50">
<p className="text-xs text-slate-600 mb-2">Need help?</p>
<a 
href={environment.webUrl + 'docs/getting-started'}
target="_blank"
className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
>
View Documentation
<ChevronRight size={14} />
</a>
</div>
</nav>
</aside>

{/* Main Content Area */}
<div className="flex-1 min-w-0">
{/* Active Tab Indicator */}
{activeTabData && (
<div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 flex items-center gap-3">
<div className="p-2 bg-white rounded-lg shadow-sm">
{React.createElement(activeTabData.icon, { 
className: 'text-indigo-600',
size: 20 
})}
</div>
<div>
<p className="font-bold text-slate-900">{activeTabData.label}</p>
<p className="text-sm text-slate-600">{activeTabData.description}</p>
</div>
</div>
)}

{/* Content Card */}
<div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 min-h-[500px]">
{renderTabContent()}
</div>

{/* Quick Actions Footer */}
<div className="mt-6 p-4 bg-white rounded-xl border border-slate-200 flex items-center justify-between">
<p className="text-sm text-slate-600">
Changes are saved automatically
</p>
<div className="flex items-center gap-2 text-sm text-green-600">
<span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
All systems operational
</div>
</div>
</div>
</div>
</div>
</main>
</div>
);
};

export default Settings;